import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Mail, Gift, ArrowRight } from 'lucide-react';

export default function GutscheinSuccessPage() {
  const [countdown, setCountdown] = useState(10);
  const [autoRedirect, setAutoRedirect] = useState(true);

  // Parse query parameters
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token') || urlParams.get('paypalOrderId') || '';
  const amount = urlParams.get('amount') || urlParams.get('value') || '';
  const orderId = urlParams.get('orderId') || urlParams.get('id') || token;
  const buyerName = urlParams.get('buyer_name') || urlParams.get('name') || '';
  const buyerEmail = urlParams.get('buyer_email') || urlParams.get('email') || '';

  useEffect(() => {
    if (autoRedirect && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (autoRedirect && countdown === 0) {
      window.location.href = '/';
    }
  }, [countdown, autoRedirect]);

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen svh-canvas flex flex-col items-center justify-center px-4 py-16">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-[560px]"
      >
        {/* Brand header */}
        <div className="text-center mb-8">
          <img
            src="/photos/Image.jpeg"
            alt="SV Heinersreuth"
            className="h-14 w-14 rounded-full object-cover mx-auto mb-3 shadow-sm ring-1 ring-black/5"
          />
          <p className="text-[15px] font-bold tracking-tight" style={{ color: 'var(--svh-ink)' }}>
            SV Heinersreuth
          </p>
          <p className="svh-label mt-1">Padel &amp; Tennis</p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="svh-card svh-card-lg svh-card-raised overflow-hidden"
        >
          <div className="h-1 w-full" style={{ background: 'var(--svh-crest)' }} />
          <div className="p-8 md:p-10">
            {/* Icon */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="flex items-center justify-center mb-6"
            >
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" strokeWidth={1.75} />
              </div>
            </motion.div>

            {/* Headline */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.25 }}
              className="text-center mb-8"
            >
              <p className="text-[12px] font-bold tracking-[0.16em] uppercase text-emerald-700 mb-3">
                Zahlung erfolgreich
              </p>
              <h1 className="svh-h2 mb-4">Ihr Gutschein ist unterwegs</h1>
              <p className="svh-body">
                Vielen Dank für Ihren Kauf. Der Gutschein wird gerade verarbeitet und in wenigen Minuten per E-Mail zugestellt.
              </p>
            </motion.div>

            {/* Amount */}
            {amount && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 }}
                className="rounded-[10px] px-5 py-4 mb-4"
                style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
              >
                <div className="flex items-center gap-3">
                  <Gift className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--svh-ink-3)' }} strokeWidth={2} />
                  <div>
                    <p className="svh-label mb-0.5">Gutscheinwert</p>
                    <p className="text-[22px] font-bold svh-num" style={{ color: 'var(--svh-ink)' }}>{amount}€</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* E-mail delivery */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.35 }}
              className="rounded-[10px] overflow-hidden mb-4"
              style={{ border: '1px solid var(--svh-border)' }}
            >
              <div className="px-5 py-4" style={{ background: 'var(--svh-surface-sub)' }}>
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--svh-ink-3)' }} strokeWidth={2} />
                  <div className="min-w-0">
                    <p className="svh-label mb-1">E-Mail-Versand</p>
                    <p className="text-[14px] leading-relaxed break-words" style={{ color: 'var(--svh-ink)' }}>
                      {buyerEmail ? (
                        <>
                          Der Gutschein wird an <strong>{buyerEmail}</strong> gesendet
                        </>
                      ) : (
                        'Der Gutschein wird in Kürze per E-Mail zugestellt'
                      )}
                    </p>
                    {buyerName && (
                      <p className="svh-meta mt-1.5">Empfänger: {buyerName}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3 px-5 py-4 bg-amber-50/70 border-t" style={{ borderColor: 'var(--svh-border)' }}>
                <span className="text-[15px] flex-shrink-0" role="img" aria-label="Spam">📬</span>
                <p className="text-[13px] leading-relaxed text-amber-900">
                  <span className="font-semibold">Keine E-Mail erhalten?</span> Bitte auch den{' '}
                  <span className="font-semibold">Spam- oder Junk-Ordner</span> prüfen — manchmal landet unsere Mail dort.
                </p>
              </div>
            </motion.div>

            {/* Transaction details */}
            {orderId && (
              <motion.details
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.4 }}
                className="rounded-[10px] px-5 py-4 mb-6"
                style={{ background: 'var(--svh-surface-sub)', border: '1px solid var(--svh-border)' }}
              >
                <summary className="cursor-pointer svh-label flex items-center justify-between gap-3">
                  <span>Transaktionsdetails</span>
                  <ArrowRight className="w-4 h-4 flex-shrink-0" />
                </summary>
                <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--svh-border)' }}>
                  <p className="svh-meta mb-1">Bestell-ID</p>
                  <p className="font-mono text-[13px] leading-relaxed break-all" style={{ color: 'var(--svh-ink-2)' }}>
                    {orderId}
                  </p>
                </div>
              </motion.details>
            )}

            {/* Next steps */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.45 }}
              className="mb-6"
            >
              <p className="svh-label mb-4">So geht&apos;s weiter</p>
              <div className="space-y-3">
                {[
                  'Sie erhalten den Gutscheincode per E-Mail',
                  'Code beim Buchen eingeben und sofort einlösen',
                  'Padel oder Tennis spielen und Spaß haben',
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3.5">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-[12px] font-bold text-white"
                      style={{ background: 'var(--svh-crest)' }}
                    >
                      {i + 1}
                    </div>
                    <p className="text-[14px] leading-snug" style={{ color: 'var(--svh-ink-2)' }}>{step}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <hr className="svh-rule mb-6" />

            {/* Actions */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.5 }}
              className="space-y-3"
            >
              <button onClick={handleGoHome} className="svh-btn svh-btn--primary svh-focus w-full">
                Zur Startseite
                <ArrowRight className="w-4 h-4" />
              </button>

              {autoRedirect && (
                <div className="text-center">
                  <p className="svh-meta">
                    Automatische Weiterleitung in {countdown} Sekunden ...
                  </p>
                  <button
                    onClick={() => setAutoRedirect(false)}
                    className="svh-btn svh-btn--secondary svh-focus w-full mt-3"
                  >
                    Abbrechen
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </motion.div>

        {/* Support note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.55 }}
          className="text-center mt-6"
        >
          <p className="svh-meta">Bei Fragen können Sie sich jederzeit an uns wenden.</p>
          <a
            href="/kontakt"
            className="svh-focus inline-flex items-center justify-center min-h-[44px] px-4 text-[13px] font-semibold underline underline-offset-4"
            style={{ color: 'var(--svh-crest)' }}
          >
            Zum Kontaktformular
          </a>
        </motion.div>

        <p className="svh-meta text-center mt-3">SV Heinersreuth e.V.</p>
      </motion.div>
    </div>
  );
}
